import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

// ─── Minimal Web Speech API typings ──────────────────────────────────────────
// `SpeechRecognition` isn't part of the standard DOM lib yet, so declare the
// bits we need locally rather than dragging in an extra @types package.

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

interface SpeechWindow extends Window {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

/**
 * Browser speech-to-text wrapper for the chat widget mic button.
 *
 * Mirrors the mobile (`@react-native-voice/voice`) flow used in
 * `new-ent-mobile-app`: single-shot dictation, emits the final transcript,
 * and lets the caller decide what to do with it (the chat widget sends it
 * straight to the chatbot service).
 *
 * Uses the prefixed `webkitSpeechRecognition` API (Chrome, Edge, modern
 * Safari). Browsers without support report `isSupported() === false` and
 * the widget hides the mic button.
 */
@Injectable({ providedIn: 'root' })
export class VoiceRecognitionService implements OnDestroy {
  private readonly recognitionCtor: SpeechRecognitionCtor | null;
  private recognition: SpeechRecognitionInstance | null = null;

  private readonly listening$ = new BehaviorSubject<boolean>(false);
  private readonly transcript$ = new Subject<string>();
  private readonly error$ = new Subject<string>();

  constructor() {
    const win = window as SpeechWindow;
    this.recognitionCtor = win.SpeechRecognition ?? win.webkitSpeechRecognition ?? null;
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  /** True if the current browser exposes SpeechRecognition. */
  isSupported(): boolean {
    return this.recognitionCtor !== null;
  }

  isListening(): Observable<boolean> {
    return this.listening$.asObservable();
  }

  /** Emits each finalised transcript (single-shot, no interim results). */
  transcripts(): Observable<string> {
    return this.transcript$.asObservable();
  }

  /** Emits a short error code/message when recognition fails. */
  errors(): Observable<string> {
    return this.error$.asObservable();
  }

  /**
   * Begin dictation. Resolves once recognition has been started; rejects if
   * the API is unsupported, mic permission was denied, or recognition fails
   * to start. Calling while already listening is a no-op.
   */
  async start(lang: string = 'en-US'): Promise<void> {
    if (!this.recognitionCtor) {
      const msg = 'Speech recognition is not supported in this browser.';
      this.error$.next(msg);
      throw new Error(msg);
    }
    if (this.listening$.value) {
      return;
    }

    // Pre-flight mic permission so the browser's permission prompt shows
    // immediately and we can surface a clean error if the user blocks it.
    // We release the stream straight away — SpeechRecognition opens its own.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch (err) {
      const msg = 'Microphone permission denied.';
      this.error$.next(msg);
      throw new Error(msg);
    }

    this.tearDown();
    const recognition = new this.recognitionCtor();
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => this.listening$.next(true);
    recognition.onend = () => this.listening$.next(false);
    recognition.onerror = (ev) => {
      this.listening$.next(false);
      this.error$.next(ev.error || 'speech-error');
    };
    recognition.onresult = (ev) => {
      const result = ev.results[ev.resultIndex];
      const transcript = result?.[0]?.transcript?.trim();
      if (transcript) {
        this.transcript$.next(transcript);
      }
    };

    this.recognition = recognition;
    try {
      recognition.start();
    } catch (err) {
      this.listening$.next(false);
      const msg = (err as Error)?.message || 'Failed to start recognition.';
      this.error$.next(msg);
      throw err;
    }
  }

  /** Stop the current dictation (a final result may still arrive). */
  stop(): void {
    try {
      this.recognition?.stop();
    } catch {
      /* ignore — recognition may already be ending */
    }
  }

  /** Abort and release the underlying recogniser. */
  destroy(): void {
    try {
      this.recognition?.abort();
    } catch {
      /* ignore */
    }
    this.tearDown();
    this.listening$.next(false);
  }

  private tearDown(): void {
    if (this.recognition) {
      this.recognition.onresult = null;
      this.recognition.onerror = null;
      this.recognition.onend = null;
      this.recognition.onstart = null;
      this.recognition = null;
    }
  }
}
